import type { BlockCommandDependencies } from './BlockBaseCommand';
import { BlockBaseCommand } from './BlockBaseCommand';
import { updateSignatureContentViaSocket } from '../grid/reduxStore/saveHandlers';
import {
  updateGridSignatureBlockState,
  updateGridPositionConfig,
  updateGridDimensionConfig,
  selectBlockContent,
} from '../grid/reduxStore/editorSlice';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { AcknowledgmentResponseStatus, ErrorCode } from '../../../services/socket/SocketEvents';
import type { SignaturePropertyUpdate } from '../../../services/socket/SocketClient';
import { rootStore } from '../grid/reduxStore/Store';

export class SignatureUpdatedCommand extends BlockBaseCommand {
  private readonly blockId: string;
  private readonly sectionId: string;
  private readonly oldProperties: SignaturePropertyUpdate;
  private readonly newProperties: SignaturePropertyUpdate;

  constructor(
    blockId: string,
    sectionId: string,
    oldProperties: SignaturePropertyUpdate,
    newProperties: SignaturePropertyUpdate,
    dependencies: BlockCommandDependencies
  ) {
    super(dependencies);
    this.blockId = blockId;
    this.sectionId = sectionId;
    this.oldProperties = oldProperties;
    this.newProperties = newProperties;
  }

  getCurrentBlockId(): string {
    return this.blockId;
  }

  async undo(): Promise<boolean> {
    return this.updateContent(this.oldProperties, this.newProperties);
  }

  async redo(): Promise<boolean> {
    return this.updateContent(this.newProperties, this.oldProperties);
  }

  private async updateContent(targetProperties: SignaturePropertyUpdate, rollbackProperties: SignaturePropertyUpdate): Promise<boolean> {
    try {
      // 1. Optimistic Update: Apply changes to Redux immediately
      this.dispatchUpdate(targetProperties);

      this.setSaving();

      // 2. Perform Socket Request
      const response = await updateSignatureContentViaSocket(this.socketClient, targetProperties);
      this.dispatch(setSaveStatusBasedOnApiResponse(response));

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatchUpdate(rollbackProperties);
        return false;
      }

      if (response.status === AcknowledgmentResponseStatus.OK) {
        return true;
      }

      this.dispatchUpdate(rollbackProperties);
      return false;
    } catch (error) {
      console.error('Failed to update signature content:', error);
      this.dispatchUpdate(rollbackProperties);
      if (error instanceof Error && error.message === ErrorCode.LOCKED) {
        this.checkDocumentLockStatus(ErrorCode.LOCKED);
        return false;
      }
      this.setNotSaved();
      return false;
    }
  }

  /**
   * Helper to dispatch Redux actions for signature properties
   */
  private dispatchUpdate(propertiesUpdate: SignaturePropertyUpdate) {
    const state = rootStore.getState();
    const blockContent = selectBlockContent(state, this.sectionId, this.blockId);

    if (blockContent && blockContent.contentSignature) {
      const updatedSignature = {
        ...blockContent.contentSignature,
        properties: propertiesUpdate.properties,
      };

      this.dispatch(
        updateGridSignatureBlockState({
          sectionId: this.sectionId,
          blockId: this.blockId,
          contentSignature: updatedSignature,
        })
      );

      this.dispatch(
        updateGridPositionConfig({
          sectionId: this.sectionId,
          blockId: this.blockId,
          x: propertiesUpdate.properties.position.x,
          y: propertiesUpdate.properties.position.y,
        })
      );

      this.dispatch(
        updateGridDimensionConfig({
          sectionId: this.sectionId,
          blockId: this.blockId,
          width: propertiesUpdate.properties.dimensions.width,
          height: propertiesUpdate.properties.dimensions.height,
        })
      );
    }
  }
}
